import MapboxMapComponent, { MapboxMapHandle } from "@/components/mapbox-map";
import { Header } from "@/components/header";
import { Card } from "@/components/ui/card";
import React, { useEffect, useMemo, useRef, useState } from "react";
import MapLocation from "@/map/location";
import { ThaloDataTable } from "@/components/thalo-data-table";
import { ColumnDef } from "@tanstack/react-table";
import {
  getEarliestSensorKitPeriod,
  getLatestSensorKitPeriod,
  installationHealthStatusFillClass
} from "@/installation/installation";
import { format, parseISO } from "date-fns";
import { OrganizationDataMinimal } from "@/organization/organization";
import { MultiSelect } from "@/components/ui/multi-select";
import { capitalizeAndReplace } from "@/lib/utils";
import { useAuthentication } from "@/contexts/AuthenticationContext";
import { useNavigate } from "@tanstack/react-router";
import { useOrganizationsMinimal } from "@/organization/api";
import { useLocations } from "@/location/api";


const ALL_ORGANIZATIONS_ASSET_TABLE_COLUMNS: ColumnDef<InstallationRow>[] = [
  {
    id: "organization",
    accessorFn: (row) => row.organization.name.startsWith("CBRE") ? "CBRE" : row.organization.name,
    header: "Organization",
    meta: {
      className: "p-2"
    }
  },
  {
    id: "location",
    accessorFn: (row) => row.location.name,
    header: "Location",
    meta: {
      className: "p-2"
    }
  },
  {
    id: "city_and_state",
    accessorFn: (row) => {
      const twoLetterState = row.location.state.includes(" ")
        ? row.location.state.split(" ").map((word: string) => word[0]).join("")
        : row.location.state;
      return `${row.location.city}, ${twoLetterState}`;
    },
    header: "City",
    meta: {
      className: "p-2"
    }
  },
  {
    id: "name",
    accessorFn: (row) => row.installation.asset.name,
    header: "Name",
    meta: {
      className: "p-2"
    }
  },
  {
    id: "config",
    accessorFn: (row) => {
      const latestSensorKitPeriod = getLatestSensorKitPeriod(row.installation);
      return latestSensorKitPeriod
        ? capitalizeAndReplace(latestSensorKitPeriod.sensor_kit.config.model)
        : "-";
    },
    header: "Config",
    meta: {
      className: "p-2"
    }
  },
  {
    id: "start",
    accessorFn: (row) => {
      const earliestSensorKitPeriod = getEarliestSensorKitPeriod(row.installation);
      return earliestSensorKitPeriod
        ? format(parseISO(earliestSensorKitPeriod.start), "MMMM do, yyyy")
        : "";
    },
    header: "Thalo monitoring installed",
    meta: {
      className: "p-2"
    }
  },
]

type InstallationRow = {
  organization: any;
  location: any;
  installation: any;
}


const AllOrganizationsPage = () => {
  const { user } = useAuthentication();
  const navigate = useNavigate();

  const { data: { results: organizations } } = useOrganizationsMinimal({ pageSize: 100 });

  const mapRef = useRef<MapboxMapHandle>(null);
  const [selectedOrganizations, setSelectedOrganizations] = useState<OrganizationDataMinimal[]>(organizations || []);

  useEffect(() => {
    mapRef.current?.reset()
  }, [selectedOrganizations]);

  const { data: { results: allLocations } } = useLocations({pageSize: 100, filters: { organization_id: selectedOrganizations.map(org => org.id)}})
  const allInstallations = allLocations.map((location) => location.installation_set).flat()
  const activeInstallations = allInstallations.filter((installation) => installation.status === "active")

  const mapLocations: MapLocation[] = allLocations.map(
    (location) => ({
      lat: location.latitude,
      lng: location.longitude,
      fillColor: installationHealthStatusFillClass[location.health_status],
    })
  )

  const rows = useMemo(() => {
    const installationRows: InstallationRow[] = []

    allLocations.forEach((location) => {
      // Find the organization for this location
      const organization = selectedOrganizations.find(org => org.id === location.organization)

      if (organization) {
        location.installation_set
          .filter((installation) => installation.status === "active")
          .forEach((installation) => {
            installationRows.push({
              organization: organization,
              location: location,
              installation: installation
            })
          })
      }
    })
    return installationRows
  }, [allLocations, selectedOrganizations])

  return (
    <div className="grid grid-cols-12 gap-x-4 gap-y-2 grid-ster">
      <div className="col-span-full flex justify-between items-center">
        <Header
          name="All monitored assets"
          showDatePicker={false}
          showChartOptions={false}
          className="font-normal text-4xl mb-4 col-span-12"
        />
        <MultiSelect
          showSelectAllAndNone
          className="lg:w-[240px]"
          placeholder="Select organizations"
          options={organizations.map((organization) => ({
            label: organization.name,
            value: organization.id
          })) || []}
          selected={
            selectedOrganizations
              .map((organization) => ({ label: organization.name, value: organization.id }))
          }
          onChange={
            (selectedOptions) => setSelectedOrganizations(
              selectedOptions
                .map(
                  (item) => organizations.find((organization) => organization.id === item.value)
                )
                .filter(
                  (org) => !!org
                )
            )
          }
          customSelectedText={`${selectedOrganizations.length} organization${selectedOrganizations.length !== 1 ? "s" : ""} selected`}
        />
      </div>
      <div className="col-span-full md:col-span-8">
        <Card className="p-4">
          <ThaloDataTable
            showColumnFilter={false}
            data={rows}
            columns={ALL_ORGANIZATIONS_ASSET_TABLE_COLUMNS}
            isLoading={!user}
            pageCount={1}
            itemsPerPage={100}
            onMouseEnter={(row) => {
              mapRef.current?.flyTo({
                lat: row.original.location.latitude,
                lng: row.original.location.longitude
              });
            }}
            onMouseLeave={(_row) => {
              mapRef.current?.reset()
            }}
            onRowClick={(row) => navigate({
              to: "/organization/$organizationId/locations/$locationId/equipment/$equipmentId",
              params: {
                locationId: row.original.location.id,
                organizationId: row.original.organization.id,
                equipmentId: row.original.installation.equipment
            }})}
          />
        </Card>
      </div>
      <div className="col-span-full md:col-span-4 ">
        <div>
          <div className="flex flex-row gap-2 mb-4">
            {
              selectedOrganizations.length > 1 && (
                <Card className="p-2">
                  <div className="flex flex-col items-center">
                    <p className="text-xl font-bold text-thalo-blueLight">
                      {selectedOrganizations.length}
                    </p>
                    <p>
                      organization{selectedOrganizations.length !== 1 && 's'}
                    </p>
                  </div>
                </Card>
              )
            }
            <Card className="p-2">
              <div className="flex flex-col items-center">
                <p className="text-xl font-bold text-thalo-blueLight">
                  {allLocations.length}
                </p>
                <p>
                  location{allLocations.length !== 1 && 's'}
                </p>
              </div>
            </Card>
            <Card className="p-2">
              <div className="flex flex-col items-center">
                <p className="text-xl font-bold text-thalo-blueLight">
                  {activeInstallations.length}
                </p>
                <p className="text-center">
                  active installation{activeInstallations.length !== 1 && 's'}
                </p>
              </div>
            </Card>
          </div>
        </div>
        <div className="grid-rows-3 grid-cols-4 grid gap-y-2 gap-x-2 h-[500px] sticky top-4">
          <MapboxMapComponent
            className="col-span-4 row-span-6"
            locations={mapLocations}
            interactive={true}
            ref={mapRef}
          />
        </div>
      </div>
    </div>
  );
};

export default AllOrganizationsPage;
